import * as React from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import {
  Delete,
  Eye,
  Pencil,
  Trash,
  Settings,
  Wallet,
  CreditCard,
} from "lucide-react";
import {
  Menu,
  MenuItem,
  IconButton,
  Checkbox,
  CircularProgress,
  Modal,
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { NavLink } from "react-router-dom";
import { Permissions } from "../../../../../permissions";
import {
  errorToast,
  infoToast,
  successToast,
} from "../../../../../services/toast.service";
import { dummyReceipts, dummyFirms } from "../../../../../dummyData";
import { useState, useEffect } from "react";

const defaultColumns = [
  { id: "receiptId", label: "Receipt ID", minWidth: "140px", required: true },
  { id: "firmName", label: "Firm Name", minWidth: "200px", required: true },
  { id: "date", label: "Date", minWidth: "120px", required: true },
  { id: "amount", label: "Amount", minWidth: "120px", required: true },
  {
    id: "paymentMode",
    label: "Payment Mode",
    minWidth: "150px",
    required: false,
  },
  {
    id: "adjustedBills",
    label: "Adjusted Bills",
    minWidth: "180px",
    required: false,
  },
  { id: "notes", label: "Notes", minWidth: "200px", required: false },
  { id: "actions", label: "Actions", minWidth: "120px", required: true },
];

export default function ReceiptTable({
  onSettingsClick,
  anchorEl,
  onCloseSettings,
  visibleColumns,
  onColumnToggle,
  setRefetchFn,
  searchQuery,
  filters,
}) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [isLoading, setIsLoading] = React.useState(false);
  const [filteredData, setFilteredData] = React.useState({
    receipts: [],
    totalCount: 0,
  });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [receiptToDelete, setReceiptToDelete] = useState(null);

  // Use dummy data instead of API
  React.useEffect(() => {
    // Simulate loading
    setIsLoading(true);

    setTimeout(() => {
      let filtered = [...dummyReceipts];

      // Apply search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(
          receipt =>
            receipt.id.toLowerCase().includes(query) ||
            receipt.firmName.toLowerCase().includes(query),
        );
      }

      // Apply payment mode filter
      if (filters.paymentMode) {
        filtered = filtered.filter(
          receipt => receipt.paymentMode === filters.paymentMode,
        );
      }

      // Apply date range filter
      if (filters.fromDate || filters.toDate) {
        filtered = filtered.filter(receipt => {
          const receiptDate = parseDate(receipt.date);

          if (filters.fromDate && filters.toDate) {
            const fromDate = parseDate(filters.fromDate);
            const toDate = parseDate(filters.toDate);
            return receiptDate >= fromDate && receiptDate <= toDate;
          } else if (filters.fromDate) {
            const fromDate = parseDate(filters.fromDate);
            return receiptDate >= fromDate;
          } else if (filters.toDate) {
            const toDate = parseDate(filters.toDate);
            return receiptDate <= toDate;
          }

          return true;
        });
      }

      // Apply amount filter
      if (filters.amount) {
        const [min, max] = filters.amount.split("-");
        const amountMin = parseFloat(min) || 0;
        let amountMax = parseFloat(max) || Infinity;

        if (filters.amount === "50000+") {
          filtered = filtered.filter(receipt => receipt.amount >= 50000);
        } else {
          filtered = filtered.filter(
            receipt =>
              receipt.amount >= amountMin && receipt.amount <= amountMax,
          );
        }
      }

      // Apply sorting
      if (filters.sortBy) {
        const [field, direction] = filters.sortBy.split("_");

        filtered.sort((a, b) => {
          let valueA, valueB;

          if (field === "firm") {
            valueA = a.firmName;
            valueB = b.firmName;
          } else if (field === "amount") {
            valueA = a.amount;
            valueB = b.amount;
          } else if (field === "date") {
            valueA = parseDate(a.date);
            valueB = parseDate(b.date);
          }

          if (direction === "asc") {
            return valueA > valueB ? 1 : -1;
          } else {
            return valueA < valueB ? 1 : -1;
          }
        });
      }

      setFilteredData({
        receipts: filtered,
        totalCount: filtered.length,
      });
      setIsLoading(false);
    }, 800); // Simulate network delay
  }, [searchQuery, filters, page, rowsPerPage]);

  // Reset to first page when filters or search changes
  useEffect(() => {
    setPage(0);
  }, [searchQuery, filters]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  // Filter visible columns
  const visibleColumnsList = defaultColumns.filter(
    column => visibleColumns[column.id],
  );

  let viewReceipt =
    Permissions.find(permission => permission.includes("show_receipts")) !==
    undefined;

  let editReceipt =
    Permissions.find(permission => permission.includes("edit_receipts")) !==
    undefined;

  let deleteReceipt =
    Permissions.find(permission => permission.includes("delete_receipts")) !==
    undefined;

  // Handle delete confirmation
  const handleOpenDeleteModal = receipt => {
    setReceiptToDelete(receipt);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setReceiptToDelete(null);
  };

  const handleConfirmDelete = () => {
    // Here you would call the API to delete the receipt
    setTimeout(() => {
      successToast(`Receipt ${receiptToDelete.id} deleted successfully`);
      handleCloseDeleteModal();
      // In a real app, you would refetch the data
      if (setRefetchFn) {
        const refetch = setRefetchFn();
        if (typeof refetch === "function") refetch();
      }
    }, 500);
  };

  // Format the adjusted bills for display
  const formatAdjustedBills = billIds => {
    if (!billIds || !billIds.length) return "-";
    return billIds.map(id => id.replace("bill", "#")).join(", ");
  };

  // Format payment mode with icon
  const renderPaymentMode = mode => {
    return (
      <div className="flex items-center justify-center gap-2">
        {mode === "Cash" ? (
          <Wallet size={16} className="text-[#5652B0]" />
        ) : (
          <CreditCard size={16} className="text-[#5652B0]" />
        )}
        <span>{mode}</span>
      </div>
    );
  };

  // Transform data to table format
  const rows = React.useMemo(() => {
    const startIndex = page * rowsPerPage;
    const displayedReceipts = filteredData.receipts.slice(
      startIndex,
      startIndex + rowsPerPage,
    );

    return displayedReceipts.map(receipt => ({
      id: receipt.id,
      receiptId: receipt.id,
      firmName: receipt.firmName,
      date: receipt.date,
      amount: `₹${receipt.amount.toLocaleString()}`,
      paymentMode: receipt.paymentMode,
      adjustedBills: formatAdjustedBills(receipt.adjustedBills),
      notes: receipt.notes || "-",
      original: receipt, // Store original data for actions
    }));
  }, [filteredData, page, rowsPerPage]);

  // Create a dummy refetch function
  React.useEffect(() => {
    if (typeof setRefetchFn === "function") {
      setRefetchFn(() => () => {
        // setIsLoading(true);
        setTimeout(() => {
          setIsLoading(false);
        }, 800);
      });
    }
  }, [setRefetchFn]);

  // Helper function to parse date strings
  const parseDate = dateStr => {
    if (!dateStr) return null;
    const [day, month, year] = dateStr.split("/");
    return new Date(year, month - 1, day);
  };

  return (
    <>
      <Paper
        sx={{
          width: "100%",
          overflow: "hidden",
          boxShadow: "none",
          border: "1px solid #E5E7EB",
          borderRadius: "0.5rem",
          display: "flex",
          flexDirection: "column",
        }}>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={onCloseSettings}
          PaperProps={{
            style: {
              maxHeight: 300,
              width: 200,
            },
          }}>
          <div className="px-3 py-2 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-600">Show Columns</p>
          </div>
          {defaultColumns.map(column => (
            <MenuItem
              key={column.id}
              onClick={() => onColumnToggle(column.id)}
              disabled={column.required}
              dense>
              <Checkbox
                checked={visibleColumns[column.id]}
                disabled={column.required}
                size="small"
                sx={{
                  color: "#5652B0",
                  "&.Mui-checked": {
                    color: "#5652B0",
                  },
                }}
              />
              <span
                className={`text-sm ${column.required ? "text-gray-400" : ""}`}>
                {column.label}
                {column.required && " *"}
              </span>
            </MenuItem>
          ))}
        </Menu>

        <div className="flex-grow flex flex-col">
          <TableContainer
            sx={{
              height: "calc(100vh - 300px)",
              borderBottom: "1px solid #E5E7EB",
            }}
            className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-50 h-full">
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {visibleColumnsList.map(column => (
                    <TableCell
                      key={column.id}
                      align="center"
                      sx={{
                        minWidth: column.minWidth,
                        backgroundColor: "#F9FAFB",
                        fontWeight: "600",
                        fontSize: "14px",
                        color: "#374151",
                        borderBottom: "2px solid #E5E7EB",
                      }}
                      className="font-outfit">
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={visibleColumnsList.length}
                      align="center">
                      <div className="flex items-center justify-center py-4">
                        <CircularProgress size={40} />
                      </div>
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={visibleColumnsList.length}
                      align="center">
                      <div className="py-4 text-gray-500">
                        No receipts found
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map(row => (
                    <TableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={row.id}
                      sx={{
                        "&:hover": {
                          backgroundColor: "#F9FAFB",
                        },
                      }}>
                      {visibleColumnsList.map(column => {
                        const value = row[column.id];
                        return (
                          <TableCell
                            key={column.id}
                            align="center"
                            sx={{
                              padding: "12px 16px",
                              fontSize: "14px",
                              color: "#4B5563",
                            }}
                            className="font-outfit">
                            {column.id === "paymentMode" ? (
                              renderPaymentMode(value)
                            ) : column.id === "actions" ? (
                              <div className="flex items-center justify-center gap-3">
                                {viewReceipt ? (
                                  <NavLink
                                    to={`/receipt/view/${row.id}`}
                                    className="p-1.5 rounded-md bg-purple-50 hover:bg-purple-100 transition-colors">
                                    <Eye size={16} className="text-[#5652B0]" />
                                  </NavLink>
                                ) : (
                                  <div className="p-1.5 rounded-md bg-gray-100 cursor-not-allowed">
                                    <Eye size={16} className="text-gray-400" />
                                  </div>
                                )}

                                {editReceipt ? (
                                  <NavLink
                                    to={`/receipt/edit/${row.id}`}
                                    className="p-1.5 rounded-md bg-purple-50 hover:bg-purple-100 transition-colors">
                                    <Pencil
                                      size={16}
                                      className="text-[#5652B0]"
                                    />
                                  </NavLink>
                                ) : (
                                  <div className="p-1.5 rounded-md bg-gray-100 cursor-not-allowed">
                                    <Pencil
                                      size={16}
                                      className="text-gray-400"
                                    />
                                  </div>
                                )}

                                {deleteReceipt ? (
                                  <button
                                    onClick={() =>
                                      handleOpenDeleteModal(row.original)
                                    }
                                    className="p-1.5 rounded-md bg-red-50 hover:bg-red-100 transition-colors">
                                    <Trash size={16} className="text-red-600" />
                                  </button>
                                ) : (
                                  <div className="p-1.5 rounded-md bg-gray-100 cursor-not-allowed">
                                    <Trash
                                      size={16}
                                      className="text-gray-400"
                                    />
                                  </div>
                                )}
                              </div>
                            ) : (
                              value
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={filteredData.totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: "1px solid #E5E7EB",
              ".MuiTablePagination-select": {
                borderRadius: "0.375rem",
              },
              marginTop: "auto",
            }}
          />
        </div>

        {isLoading && (
          <div className="absolute top-0 left-0 right-0">
            <div className="h-1 w-full overflow-hidden">
              <div className="animate-progress bg-[#5652B0] h-1"></div>
            </div>
          </div>
        )}
      </Paper>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            maxWidth: "400px",
            width: "100%",
            boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
          },
        }}>
        <DialogTitle
          sx={{
            fontSize: "1.25rem",
            fontWeight: 600,
            color: "#1F2937",
            borderBottom: "1px solid #E5E7EB",
            padding: "20px 24px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}>
          <Trash size={22} className="text-red-500" />
          Delete Receipt
        </DialogTitle>
        <DialogContent sx={{ padding: "24px" }}>
          <DialogContentText className="text-gray-600 mb-2">
            Are you sure you want to delete receipt{" "}
            <span className="font-semibold">{receiptToDelete?.id}</span> for{" "}
            <span className="font-semibold">{receiptToDelete?.firmName}</span>?
          </DialogContentText>
          <DialogContentText className="text-red-500 text-sm">
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            padding: "16px 24px 24px",
            justifyContent: "flex-end",
            gap: "12px",
          }}>
          <Button
            onClick={handleCloseDeleteModal}
            sx={{
              color: "rgba(0, 0, 0, 0.6)",
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "8px",
              padding: "8px 16px",
            }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              bgcolor: "#EF4444",
              "&:hover": { bgcolor: "#DC2626" },
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "8px",
              padding: "8px 20px",
              boxShadow: "none",
            }}>
            Delete Receipt
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export { defaultColumns };
